/* eslint-disable jsx-a11y/label-has-associated-control */
import type { ChangeEvent, TextareaHTMLAttributes } from 'react';
import React, { useEffect, useRef, useState } from 'react';

import type { InputFeedback } from '../Feedback/Feedback';
import { FieldWrapper } from '../FieldWrapper/FieldWrapper';
import * as styles from './TextField.styles';
import { getAriaDescribedByForInputField } from 'client-ribbons-design-system/utils/getAriaDescribedByForInputField';

export interface TextFieldProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'type'> {
  $completed?: boolean;
  description?: string;
  error?: string | null;
  feedback?: InputFeedback;
  id: string;
  label: string;
  labelHidden?: boolean;
}

export const TEXT_AREA_INPUT_CLASSNAME = 'text-area__input';

export const TextField = (props: TextFieldProps) => {
  const {
    $completed,
    className,
    description,
    error,
    feedback,
    id,
    label,
    labelHidden = false,
    onChange = () => {},
    required,
    value,
  } = props;
  const textareaRef = useRef<null | HTMLTextAreaElement>(null);
  const [textAreaHeight, setTextAreaHeight] = useState('auto');

  useEffect(() => {
    if (textareaRef.current) {
      const { borderBottomWidth, borderTopWidth } = getComputedStyle(textareaRef.current, null);
      const heightAdjustment = parseInt(borderTopWidth, 10) + parseInt(borderBottomWidth, 10);
      setTextAreaHeight(`${textareaRef.current.scrollHeight + heightAdjustment}px`);
    }
  }, [value]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setTextAreaHeight('auto');
    onChange(e);
  };

  const fieldId = `${id}-textarea`;

  return (
    <div className={className}>
      <FieldWrapper
        $completed={$completed}
        description={description}
        error={error}
        feedback={feedback}
        fieldId={fieldId}
        label={label}
        labelHidden={labelHidden}
      >
        <textarea
          aria-describedby={getAriaDescribedByForInputField({
            fieldId,
            description,
            error,
          })}
          aria-invalid={!!error}
          aria-label={label}
          aria-required={required}
          className={TEXT_AREA_INPUT_CLASSNAME}
          css={styles.textarea}
          onChange={handleChange}
          ref={textareaRef}
          rows={1}
          style={{
            height: textAreaHeight,
          }}
          {...props}
          id={fieldId}
        />
      </FieldWrapper>
    </div>
  );
};
